import { useEffect, useState } from 'react';
import type { ManualStudentInvitePreview } from '@court-sync/shared';
import { publicBookingService } from '../services/publicBooking';

export type ManualInvitePreviewStatus = 'idle' | 'loading' | 'valid' | 'invalid';

export interface UseManualInvitePreviewResult {
  status: ManualInvitePreviewStatus;
  preview: ManualStudentInvitePreview | null;
}

type FetchState = {
  forToken: string;
  status: Exclude<ManualInvitePreviewStatus, 'idle' | 'loading'>;
  preview: ManualStudentInvitePreview | null;
};

/** Loads roster invite context from `?studentInvite=` for signup prefill and contextual copy. */
export function useManualInvitePreview(token: string | null | undefined): UseManualInvitePreviewResult {
  const trimmed = token?.trim() ?? '';
  const [fetchState, setFetchState] = useState<FetchState | null>(null);

  useEffect(() => {
    if (!trimmed) return undefined;

    let cancelled = false;

    void publicBookingService.getManualInvitePreview(trimmed).then((res) => {
      if (cancelled) return;
      if (!res.success || !res.data) {
        setFetchState({ forToken: trimmed, status: 'invalid', preview: null });
        return;
      }
      setFetchState({ forToken: trimmed, status: 'valid', preview: res.data });
    }).catch(() => {
      if (cancelled) return;
      setFetchState({ forToken: trimmed, status: 'invalid', preview: null });
    });

    return () => {
      cancelled = true;
    };
  }, [trimmed]);

  if (!trimmed) {
    return { status: 'idle', preview: null };
  }

  if (!fetchState || fetchState.forToken !== trimmed) {
    return { status: 'loading', preview: null };
  }

  return { status: fetchState.status, preview: fetchState.preview };
}

export function formatManualInviteStudentName(
  preview: Pick<ManualStudentInvitePreview, 'studentFirstName' | 'studentLastName'>
): string {
  const name = `${preview.studentFirstName} ${preview.studentLastName}`.trim();
  return name || 'your student';
}
